import type { ArtifactListingEntry } from '@crowdex/agent-contracts/api';
import type { ToolRef } from '@crowdex/plugin-contracts';
import { Bot, Link2, type LucideIcon } from 'lucide-react';
import { useMemo, useSyncExternalStore } from 'react';
import { notificationTarget } from '@/features/notifications/notifications';
import { pluginIcon } from '@/features/plugins/plugin-icons';
import type { PluginWorkbench } from '@/features/plugins/plugin-workbench';
import { useWorkbench } from '@/features/plugins/use-plugin-workbench';
import { appNameOf, artifactOfRef, type RefPlace, refPlace } from '@/lib/conversations/tool-refs';
import { dockAppIn } from '@/lib/desktop-shell/app-panels';
import { useConversationOpener } from '@/lib/desktop-shell/conversation-opener';
import { dockArtifact } from '@/lib/desktop-shell/open-artifact';
import type { DesktopShellState } from '@/lib/desktop-shell/store';
import { useOptionalDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import { activePanelTab } from '@/lib/side-panel/panel-tabs';
import { dockArtifactTarget } from './dock-artifact-target';
import { remoteItemPath } from './remote-app-bridge';
import { openRemoteAppItem } from './remote-app-views';

/** What a ref's app is, for its card and its row: its name, its icon, its tab when it has one. */
export type RefApp = {
  title: string;
  icon: LucideIcon;
  iconSrc?: string | undefined;
  /** Its tab, where its items dock beside the conversation: an app or an editor. */
  panel: PluginWorkbench['panels'][number] | null;
};

/** A ref's app as the user's workbench knows it (or by its name, when it is not installed). */
export function refApp(
  app: string,
  workbench: Pick<PluginWorkbench, 'panels' | 'pluginDisplayNames' | 'services'>,
): RefApp {
  const panel =
    workbench.panels.find(
      (candidate) => candidate.plugin === app && candidate.rail !== undefined,
    ) ?? null;
  const service = workbench.services?.find((candidate) => candidate.plugin === app);
  return {
    title: workbench.pluginDisplayNames[app] ?? service?.title ?? panel?.title ?? appNameOf(app),
    icon: app === 'crowdex' ? Bot : panel !== null ? pluginIcon(panel.icon ?? 'app') : Link2,
    iconSrc: panel?.iconSrc ?? service?.iconSrc,
    panel,
  };
}

/** Opens a link at its service, in a new tab (or the browser of the desktop app). */
export function openExternal(url: string): void {
  window.open(url, '_blank', 'noopener,noreferrer');
}

/**
 * What the side panel shows now, for the preview card of it to say so: an artifact (its id), or
 * an app's tab on its item or route. Nothing while the panel is closed.
 */
export type PanelShown =
  | { artifact: string }
  | { view: string; item: string | null; route: string | null }
  | null;

export function panelShown(
  state: Pick<DesktopShellState, 'sidePanelOpen' | 'panelTabs' | 'appPanelTargets'>,
): PanelShown {
  if (!state.sidePanelOpen) return null;
  const tab = activePanelTab(state.panelTabs);
  if (tab === null) return null;
  if (tab.view.startsWith('artifact:')) return { artifact: tab.view.slice('artifact:'.length) };
  const target = state.appPanelTargets[tab.id];
  return { view: tab.view, item: target?.item ?? null, route: target?.route ?? null };
}

/**
 * Whether `shown` is the thing a ref names: its artifact (`artifactId`), or its item in its app's
 * tab (`panelView`), by its id, its route, or its page's path (a Crowlingo application's).
 */
export function refShown(
  ref: ToolRef,
  artifactId: string | undefined,
  panelView: string | null,
  shown: PanelShown,
): boolean {
  if (shown === null) return false;
  if ('artifact' in shown) return artifactId !== undefined && artifactId === shown.artifact;
  if (ref.kind !== 'item' || panelView === null || shown.view !== panelView) return false;
  if (shown.item === ref.id) return true;
  if (shown.route === null) return false;
  if (ref.route !== undefined && shown.route === ref.route) return true;
  const link = ref.route ?? ref.url;
  return link !== undefined && remoteItemPath(link) === shown.route;
}

const NOTHING_SHOWN = 'null';
const noSubscription = () => () => undefined;

/** `panelShown` of the shell's store, followed as the panel changes (null outside a shell). */
function usePanelShown(store: ReturnType<typeof useOptionalDesktopShellStoreApi>): PanelShown {
  // A string snapshot: the same thing shown is the same value, so nothing renders again for it.
  const key = useSyncExternalStore(
    store?.subscribe ?? noSubscription,
    () => (store === null ? NOTHING_SHOWN : JSON.stringify(panelShown(store.getState()))),
    () => NOTHING_SHOWN,
  );
  return useMemo(() => JSON.parse(key) as PanelShown, [key]);
}

/**
 * How the conversation shows and opens what it touched: one rule for its previews and its
 * Summary's rows. An app's or an editor's opens docked in the conversation's side panel (an
 * artifact on its item, an app's item through its tab); a service's at the service ↗; a task is
 * a conversation, opened.
 */
export function useRefTargets(artifacts: readonly ArtifactListingEntry[] = []) {
  const workbench = useWorkbench();
  const store = useOptionalDesktopShellStoreApi();
  const opener = useConversationOpener();
  const shown = usePanelShown(store);
  return useMemo(() => {
    const appOf = (app: string) => refApp(app, workbench);
    /** The ref's item (or `artifactId`) is what the side panel shows now. */
    const isShown = (ref: ToolRef, artifactId?: string) =>
      refShown(ref, artifactId, appOf(ref.app).panel?.view ?? null, shown);
    const placeOf = (ref: ToolRef): RefPlace => refPlace(ref, (app) => appOf(app).panel !== null);
    const open = (ref: ToolRef) => {
      const place = placeOf(ref);
      if (place === 'work') {
        if (ref.app === 'crowdex' && opener !== null) void opener.openConversation(ref.id);
        else if (ref.url !== undefined) openExternal(ref.url);
        return;
      }
      if (place === 'service' || store === null) {
        if (ref.url !== undefined) openExternal(ref.url);
        return;
      }
      if (place === 'artifact') {
        const artifact = artifactOfRef(ref, artifacts);
        if (artifact !== undefined) dockArtifact(store, artifact.id);
        else if (ref.path !== undefined)
          void dockArtifactTarget(store, {
            plugin: ref.app,
            space: ref.space ?? 'workspace',
            path: ref.path,
            conversation: store.getState().chatId,
          });
        else if (ref.url !== undefined) openExternal(ref.url);
        return;
      }
      const panel = appOf(ref.app).panel;
      if (panel === null) {
        if (ref.url !== undefined) openExternal(ref.url);
        return;
      }
      // A Crowlingo application (CrowDrive, CrowBox): its page embedded, on the item's deep link.
      const link = ref.route ?? ref.url;
      if (
        panel.remoteView !== undefined &&
        link !== undefined &&
        openRemoteAppItem(store, workbench.panels, { plugin: ref.app, url: link })
      )
        return;
      // An app's own tab, on its item (and its route, `?item=…#/…`, when it names one).
      const target =
        ref.route === undefined
          ? null
          : notificationTarget({ plugin: ref.app, link: ref.route }, [panel])?.target;
      dockAppIn(store, store.getState().chatId, panel.view, target ?? { item: ref.id });
    };
    return { appOf, placeOf, open, isShown, shown };
  }, [workbench, store, opener, artifacts, shown]);
}
